import { deepSortObject } from 'apis/utils';
import { AGGREGATION_TYPE } from 'components/View/constants';
import {
  areDataQuarterSelectionsEqual,
  createDefaultDataQuarterSelection,
} from 'components/View/filters/DataQuarterFilter/helpers';

import {
  CMA_SUMMARY_INITIAL_FILTERS,
  CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE,
  CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS,
  CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS,
  CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS,
  CMA_SUMMARY_ROOM_TYPE_GROUPING,
} from './constants';
import { CMASummaryFilterField, CMASummaryFilters, CMASummaryFiltersOutbound } from './types';

export const transformCMASummaryFiltersOutbound = (filtersInboud: CMASummaryFilters) => {
  const viewOptions = filtersInboud.viewOptions ?? CMA_SUMMARY_INITIAL_FILTERS.viewOptions;

  return {
    room_comparison_type: filtersInboud.roomComparisonType,
    room_comparison_rows:
      filtersInboud.roomComparisonRows?.map((row) => ({
        fields: row.map((field) => ({ name: field.name, type: field.fieldType })),
      })) ?? [],
    room_comparison_sqft_range: filtersInboud.roomComparisonSqftRange,
    room_comparison_show_beyond_range: filtersInboud.roomComparisonShowClosestBeyondRange,
    room_type_filter_comparison_type: filtersInboud.roomTypeFilterComparisonType,
    room_types_price_display: filtersInboud.roomTypesPriceDisplay,
    view_options: {
      contact_information: viewOptions?.contactInformation,
      address: filtersInboud?.viewOptions?.address,
      website: filtersInboud?.viewOptions?.website,
      distance: filtersInboud?.viewOptions?.distance,
      phone: filtersInboud?.viewOptions?.phone,
      year_opened: filtersInboud?.viewOptions?.yearOpened,
      community_notes: filtersInboud?.viewOptions?.communityNotes,
      reviews: viewOptions?.reviews,
      shop_date: filtersInboud?.viewOptions?.shopDate,
      occupancy: filtersInboud?.viewOptions?.occupancy,
      contract_benefits: viewOptions?.contractBenefits,
      care_types: viewOptions?.careTypes,
      rates_fees: filtersInboud?.viewOptions?.ratesFees,
      room_type: viewOptions?.roomType,
      sq_footage: viewOptions?.squareFootage,
      price_per_sq: viewOptions?.pricePerSquareFootage,
      community_fee: viewOptions?.communityFee,
      pet_fee: viewOptions?.petFee,
      second_person_fee: viewOptions?.secondPersonFee,
      parking_fee: viewOptions?.parkingFee,
      respite_fee: viewOptions?.respiteFee,
      additional_fee: viewOptions?.additionalFee,
      second_person_entrance_fee: filtersInboud?.viewOptions?.secondPersonEntranceFee,
      move_in_fee: viewOptions?.moveInFee,
      deposit: viewOptions?.deposit,
      emergency_pendant_fee: viewOptions?.emergencyPendantFee,
      care_fee: viewOptions?.careFee,
      medication_fee: viewOptions?.medicationFee,
      fee_note: viewOptions?.feeNote,
      medication_fee_note: viewOptions?.medicationFeeNote,
      incentives: viewOptions?.incentives,
      average_competitors: viewOptions?.averageCompetitors,
      competitor_display_mode: filtersInboud?.viewOptions?.competitorDisplayMode,
      value_display: viewOptions?.valueDisplay,
    },
    // Don't save competitors until we fix the CommonFilters handling of competitors for different communities
    competitors: [],
    review_sources: filtersInboud.reviewSources ?? [],
    care_types: filtersInboud.careTypes ?? [],
    room_types: filtersInboud.roomTypes ?? [],
    room_type_grouping: filtersInboud.roomTypeGrouping,
    care_levels: filtersInboud.careLevels ?? [],
    care_levels_stats_type: filtersInboud.careLevelsStatsType ?? AGGREGATION_TYPE.AVG,
    medication_levels: filtersInboud.medicationLevels ?? [],
    medication_levels_stats_type: filtersInboud.medicationLevelsStatsType ?? AGGREGATION_TYPE.AVG,
    contract_types: filtersInboud.contractTypes ?? [],
    contract_display_type: filtersInboud?.contractDisplayType ?? null,
    contract_selection_type: filtersInboud?.contractSelectionType ?? null,
    community_contract_slugs: filtersInboud?.communityContractSlugs ?? [],
    selected_quarter_slug: filtersInboud?.dataQuarterSelection?.selectedQuarterSlug ?? null,
    include_prior_competitors: filtersInboud?.dataQuarterSelection?.includePriorCompetitors,
  };
};

const transformCMAFilterViewOptionsInbound = (viewOptions: any) =>
  viewOptions
    ? {
        contactInformation: viewOptions.contact_information,
        address: viewOptions.address,
        website: viewOptions.website,
        distance: viewOptions.distance,
        phone: viewOptions.phone,
        yearOpened: viewOptions.year_opened,
        communityNotes: viewOptions.community_notes,
        reviews: viewOptions.reviews,
        contractBenefits: viewOptions.contract_benefits,
        careTypes: viewOptions.care_types,
        ratesFees: viewOptions.rates_fees,
        roomType: viewOptions.room_type,
        squareFootage: viewOptions?.sq_footage,
        pricePerSquareFootage: viewOptions.price_per_sq,
        shopDate: viewOptions?.shop_date,
        occupancy: viewOptions?.occupancy,
        communityFee: viewOptions.community_fee,
        petFee: viewOptions.pet_fee,
        secondPersonFee: viewOptions.second_person_fee,
        parkingFee: viewOptions.parking_fee,
        respiteFee: viewOptions.respite_fee,
        additionalFee: viewOptions.additional_fee,
        moveInFee: viewOptions.move_in_fee,
        secondPersonEntranceFee: viewOptions.second_person_entrance_fee,
        deposit: viewOptions.deposit,
        emergencyPendantFee: viewOptions.emergency_pendant_fee,
        careFee: viewOptions.care_fee,
        medicationFee: viewOptions.medication_fee,
        feeNote: viewOptions.fee_note,
        medicationFeeNote: viewOptions.medication_fee_note ?? viewOptions.fee_note,
        incentives: viewOptions.incentives,
        averageCompetitors: viewOptions.average_competitors ?? CMA_SUMMARY_INITIAL_FILTERS.viewOptions.averageCompetitors,
        competitorDisplayMode: viewOptions.competitor_display_mode,
        valueDisplay: viewOptions.value_display,
      }
    : CMA_SUMMARY_INITIAL_FILTERS.viewOptions;

export const transformCMASummaryFiltersInbound = (filtersOutbound: CMASummaryFiltersOutbound) => ({
  roomComparisonType: filtersOutbound.room_comparison_type
    ? filtersOutbound.room_comparison_type
    : CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_OPTIONS.SIMPLE_COMPARISON,
  roomComparisonRows: filtersOutbound.room_comparison_rows?.map((row) =>
    row.fields.map((field) => ({
      name: field.name,
      fieldType: field.type,
    }))
  ),
  roomComparisonSqftRange: filtersOutbound.room_comparison_sqft_range ?? CMA_SUMMARY_ROOM_COMPARISON_DEFAULT_SQFT_RANGE,
  roomComparisonShowClosestBeyondRange: Boolean(filtersOutbound.room_comparison_show_beyond_range),
  roomTypeFilterComparisonType:
    filtersOutbound.room_type_filter_comparison_type ?? CMA_SUMMARY_ROOM_TYPE_FILTER_COMPARISON_OPTIONS.BY_ROOM_TYPE,
  competitors: [],
  viewOptions: transformCMAFilterViewOptionsInbound(filtersOutbound.view_options),
  reviewSources: filtersOutbound.review_sources,
  careTypes: filtersOutbound.care_types,
  roomTypes: filtersOutbound.room_types,
  roomTypeGrouping: filtersOutbound.room_type_grouping
    ? filtersOutbound.room_type_grouping
    : CMA_SUMMARY_ROOM_TYPE_GROUPING.GROUP,
  roomTypesPriceDisplay: filtersOutbound.room_types_price_display ?? CMA_SUMMARY_INITIAL_FILTERS.roomTypesPriceDisplay,
  careLevels: filtersOutbound.care_levels ?? [],
  careLevelsStatsType: filtersOutbound.care_levels_stats_type ?? CMA_SUMMARY_INITIAL_FILTERS.careLevelsStatsType,
  medicationLevels: filtersOutbound.medication_levels ?? [],
  medicationLevelsStatsType:
    filtersOutbound.medication_levels_stats_type ?? CMA_SUMMARY_INITIAL_FILTERS.medicationLevelsStatsType,
  contractTypes: filtersOutbound.contract_types,
  contractDisplayType: filtersOutbound.contract_display_type,
  contractSelectionType: filtersOutbound.contract_selection_type,
  communityContractSlugs: filtersOutbound.community_contract_slugs,
  dataQuarterSelection: {
    ...createDefaultDataQuarterSelection(),
    selectedQuarterSlug: filtersOutbound?.selected_quarter_slug ?? null,
    includePriorCompetitors: filtersOutbound?.include_prior_competitors,
  },
});

export const filterRemainingCMASummaryRoomComparisonFields = (row: CMASummaryFilterField[]) =>
  Object.values(CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS).filter((fieldName) => {
    if (!row) {
      return true;
    }
    return !row?.find((field: CMASummaryFilterField) => field.name === fieldName);
  });

export const areCMASummaryFiltersEqual = (leftFilters: CMASummaryFilters, rightFilters: CMASummaryFilters) => {
  if (!leftFilters || !rightFilters) {
    return false;
  }

  const normalizedLeft = deepSortObject(transformCMASummaryFiltersOutbound(leftFilters));
  const normalizedRight = deepSortObject(transformCMASummaryFiltersOutbound(rightFilters));

  if (!areDataQuarterSelectionsEqual(leftFilters?.dataQuarterSelection, rightFilters?.dataQuarterSelection)) {
    return false;
  }

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};
